// @flow

import * as React from 'react';
import { bind } from '@ta/common.api';
import AttachedArrowOverlay from '@ta/overlays.attached-arrow-overlay';
import { Closer, Global } from '@ta/overlays.managers';
import { CloseIcon as CloseX } from '@ta/modal.core';
import trackEventAJAX from '@ta/common.tracking';
import EventBoundary from '@ta/events.event-boundary';
import { localize } from '@ta/common.i18n';
import { actions } from '@ta/common.state';
import GlobalNavContext, { type ContextValue } from '@ta/brand.global-nav-context';

import styles from './ShoppingCartRecapture.less';

type ComponentProps = {
  /** CSS selector for an element to attach this overlay below it. */
  below: ?HTMLElement,

  /** The event dispatching function to show the modal */
  showModal: (?string) => void,

  /** The header text for the recapture popover */
  headerText: string,

  /** The body text for the recapture popover */
  bodyText: string,

  /** The cookie ID to log to /AttractionCommerceAjax when the recapture is shown to the user */
  cookieId: string,

  /** The page property to log when the popover is dismissed */
  pageProperty: string,

  /** Callback for if/when the recapture is shown */
  onShow?: () => any,
};

const GLOBAL_MODAL_NAME = 'shopping_cart_recapture';

/**
 * The UI Component for the shopping cart recapture.
 */
export class ShoppingCartRecaptureComponent extends React.Component<ComponentProps> {
  static defaultProps = {
    onShow: undefined,
  };

  componentDidMount() {
    // show the modal
    this.props.showModal(GLOBAL_MODAL_NAME);

    // request to mark session as seen
    fetch(`/AttractionCommerceAjax?a=${this.props.cookieId}`, {
      // we don't actually care about sending cookies, but need this in order to set cookies returned in the response
      credentials: 'same-origin',
    });

    // if we have a callback, call it!
    if (this.props.onShow) {
      this.props.onShow();
    }
  }

  dismissModal = () => {
    trackEventAJAX({
      module: this.props.pageProperty,
      action: 'close',
    });

    // dismiss the modal
    this.props.showModal(null);
  };

  render() {
    // if we don't have a selector to position the recapture below, don't do anything
    if (!this.props.below) {
      return null;
    }
    const { below } = this.props;
    return (
      <GlobalNavContext.Consumer>
        {(context: ContextValue) => (
          <Global name={GLOBAL_MODAL_NAME}>
            <EventBoundary events={['onClick']}>
              <div onClick={this.dismissModal}>
                <AttachedArrowOverlay
                  target={below}
                  position="below"
                  shiftY={4}
                  fixed={context.usePersistentIcons}
                >
                  <div className={styles.inner}>
                    <div className={styles.header_text}>{this.props.headerText}</div>
                    <div className={styles.body_text}>{this.props.bodyText}</div>
                    <Closer>{close => <CloseX onClick={close} />}</Closer>
                  </div>
                </AttachedArrowOverlay>
              </div>
            </EventBoundary>
          </Global>
        )}
      </GlobalNavContext.Consumer>
    );
  }
}

type RecaptureType = 'abandoned' | 'education' | 'none';

type ExternalRecaptureTypeProps = {|
  /** The recapture type */
  recaptureType: RecaptureType,

  /** CSS selector for an element to attach this overlay below it. */
  below: ?HTMLElement,
|};

type RecaptureTypeProps = {|
  ...ExternalRecaptureTypeProps,

  /** The event dispatching function to show the modal */
  showModal: (?string) => void,
|};

/**
 * Intermediate component that sets the content and tracking props based on 'recaptureType'
 */
const RecaptureTypeComponent = (props: RecaptureTypeProps) => {
  switch (props.recaptureType) {
    case 'abandoned':
      return (
        <ShoppingCartRecaptureComponent
          below={props.below}
          showModal={props.showModal}
          headerText={localize('abandon_cart_home_banner_2350')}
          bodyText={localize('attractions.cart_recapture.abandoned_cart_overlay_cta')}
          cookieId="rso"
          pageProperty="cart_recapture"
        />
      );
    case 'education':
      return (
        <ShoppingCartRecaptureComponent
          below={props.below}
          showModal={props.showModal}
          headerText={localize('attractions.cart_recapture.alert.headline')}
          bodyText={localize('attractions.cart_recapture.alert_body')}
          cookieId="rsa"
          pageProperty="cart_education"
        />
      );
    default:
      // default to doing nothing, includes the case of recaptureType === 'none'
      return null;
  }
};

/**
 * The container for the recapture alert which gets the recapture type from the API
 */
export default bind<RecaptureTypeProps, ExternalRecaptureTypeProps, _, _, _, _>({
  data: (state, stage) => {
    const staged = stage('/data/1.0/cart/recaptureAlertType');

    return (newState, props: ExternalRecaptureTypeProps) => {
      const { data } = staged.get();
      const { type: recaptureType = 'none' } = data || { type: 'none' };
      const { below = null } = props || { below: null };
      return {
        recaptureType,
        below,
      };
    };
  },
  actions: {
    showModal: actions.overlays.showGlobalOverlay,
  },
})(RecaptureTypeComponent);
